import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center text-teal-600 hover:text-teal-700 mb-6"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Home
        </Link>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Wash & Fold Terms & Conditions
          </h1>
          <p className="text-gray-500 mb-8">Family Laundry LLC</p>
          <p className="text-sm text-gray-400 mb-8">Last Updated: January 2025</p>

          <div className="prose prose-gray max-w-none">
            
            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Service Description</h2>
              <p className="text-gray-700 leading-relaxed">
                Family Laundry LLC ("Company," "we," "us," or "our") provides wash and fold laundry services 
                with free pickup and delivery within our service areas: Lawrenceville, Lilburn, Norcross, 
                and Snellville, Georgia.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Pricing & Payment</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Services are charged based on selected package or per-pound rate ($1.50/lb)</li>
                <li>Payment can be made online at the time of booking ("Pay Now") or upon pickup/delivery ("Pay Later")</li>
                <li>All prices are in US Dollars and are subject to change without prior notice</li>
                <li>Payments are processed securely through Stripe</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Pickup & Delivery</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Pickup times are available between 9:00 AM and 4:00 PM</li>
                <li>Same-day service available for qualifying orders picked up before 4:00 PM</li>
                <li>Pickups after 4:00 PM will be delivered the following day</li>
                <li>Customer must ensure items are ready and accessible at the scheduled pickup time</li>
                <li>We reserve the right to reschedule pickups due to unforeseen circumstances</li>
              </ul>
            </section>

            {/* Section 4 - Important */}
            <section className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-amber-800 mb-4">4. Limitation of Liability</h2>
              <p className="text-amber-900 leading-relaxed font-medium">
                Family Laundry LLC is NOT responsible for:
              </p>
              <ul className="list-disc pl-6 text-amber-800 space-y-2 mt-3">
                <li><strong>Loss</strong> of any items left in pockets or attached to garments</li>
                <li><strong>Damage</strong> due to pre-existing conditions, improper labeling, or items not suitable for machine washing</li>
                <li><strong>Shrinkage</strong> that may occur during the normal washing and drying process</li>
                <li><strong>Normal wear and tear</strong> including fading, pilling, or deterioration of fabric</li>
                <li><strong>Color bleeding</strong> from items that are not colorfast</li>
                <li><strong>Damage to delicate items</strong> not properly identified by the customer</li>
              </ul>
              <p className="text-amber-900 mt-4 font-semibold">
                In the event of proven negligence by Family Laundry LLC, liability is limited to 
                the cost of the service provided, not the replacement value of the item(s).
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Customer Responsibilities</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Empty all pockets before pickup</li>
                <li>Identify and separate delicate or special care items</li>
                <li>Inform us of any stains or special treatment requirements</li>
                <li>Provide accurate contact and address information</li>
                <li>Be available during scheduled pickup and delivery windows</li>
              </ul>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Items We Do Not Accept</h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Items contaminated with hazardous materials</li>
                <li>Items with excessive pet hair (additional charges may apply)</li>
                <li>Heavily soiled or biohazard items</li>
                <li>Items requiring dry cleaning (unless specified)</li>
                <li>Leather, suede, or fur garments</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cancellation Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                Cancellations must be made at least 2 hours before the scheduled pickup time. 
                Failure to cancel may result in a service fee. Refunds for prepaid services 
                will be processed within 5-7 business days.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Loyalty Program</h2>
              <p className="text-gray-700 leading-relaxed">
                Registered customers earn loyalty points with each completed wash & fold service. 
                After 10 completed services, the 11th service is FREE. Loyalty points are 
                non-transferable and have no cash value.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                We collect and use personal information solely for providing our services. 
                Your information will not be sold or shared with third parties except as 
                required to complete your service (e.g., payment processing).
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                Any disputes arising from these terms shall be resolved through good faith 
                negotiation. If unresolved, disputes will be subject to the laws of the 
                State of Georgia, with venue in Gwinnett County.
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Contact Information</h2>
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-gray-700 font-semibold">Family Laundry LLC</p>
                <p className="text-gray-600">5265 Jimmy Carter Blvd Suite 6230</p>
                <p className="text-gray-600">Norcross, GA 30093</p>
                <p className="text-gray-600 mt-2">
                  📞 Phone: <a href="tel:762-436-2067" className="text-teal-600">762-436-2067</a>
                </p>
                <p className="text-gray-600">
                  ✉️ Email: <a href="mailto:familylaundry5265@gmail.com" className="text-teal-600">familylaundry5265@gmail.com</a>
                </p>
              </div>
            </section>

            {/* Agreement Statement */}
            <section className="mt-10 pt-8 border-t border-gray-200">
              <p className="text-gray-600 text-sm leading-relaxed">
                By using our services, you acknowledge that you have read, understood, and agree 
                to be bound by these Terms & Conditions. We reserve the right to modify these 
                terms at any time. Continued use of our services constitutes acceptance of any changes.
              </p>
            </section>

          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-8">
          <Link 
            to="/#booking" 
            className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Book Your Pickup Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
